import AsyncStorage from '@react-native-async-storage/async-storage'
import axios from 'axios'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { Platform } from 'react-native'
import analytics from '../../../domain/services/analytics/AnalyticsService'
import Configuration from '../../../domain/services/ConfigurationService'
import LoadingScreen from '../screens/LoadingScreen'

enum AuthStatus {
  LOADING,
  SUCCESS,
  ERROR,
}

const AuthContext = createContext(null)
const { Provider } = AuthContext
const REFRESH_TOKEN = 'auth0.refreshToken'

const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string>(null)
  const [refreshToken, setRefreshToken] = useState<string>(null)
  const [status, setStatus] = useState<AuthStatus>(AuthStatus.LOADING)

  const redirectUri =
    Platform.OS === 'ios'
      ? `${Configuration.SCHEME}://login-callback`
      : `${Configuration.CABANA_UI}/login-callback`

  const init = () => {
    return AsyncStorage.getItem(REFRESH_TOKEN).then((refreshToken) => {
      setRefreshToken(refreshToken)
      return refreshToken
    })
  }

  const linkForLogin = (email) => {
    axios
      .post(`https://${Configuration.AUTH0_DOMAIN}/passwordless/start`, {
        client_id: Configuration.AUTH0_CLIENT_ID,
        connection: 'email',
        email: email,
        send: 'link',
        authParams: {
          audience: Configuration.AUTH0_AUDIENCE,
          response_type: 'code',
          redirect_uri: redirectUri,
        },
      })
      .then((response) => {
        analytics.track('Passwordless Start', {
          'Method Used': 'Email',
          Status: 'Success',
        })
        return response
      })
      .catch((error) => {
        analytics.track('Passwordless Start', {
          'Method Used': 'Email',
          Status: 'Failure',
        })

        const error_description = error.response.data.error_description
        return Promise.reject(error_description)
      })
  }

  const loginWithCode = (code) => {
    return updateTokens(
      axios
        .post(`https://${Configuration.AUTH0_DOMAIN}/oauth/token`, {
          client_id: Configuration.AUTH0_CLIENT_ID,
          grant_type: 'authorization_code',
          code,
          audience: Configuration.AUTH0_AUDIENCE,
          scope: 'openid profile email offline_access',
          redirect_uri: redirectUri,
        })
        .then((response) => {
          analytics.track('Logged In', {
            'Method Used': 'Passwordless',
            Status: 'Success',
          })
          return response
        })
        .catch((error) => {
          analytics.track('Logged In', {
            'Method Used': 'Passwordless',
            Status: 'Failure',
          })

          const error_description = error.response.data.error_description
          return Promise.reject(
            'Passwordless login error - ' + error_description
          )
        })
    )
  }

  const loginWithEmailPassword = (email, password) => {
    return updateTokens(
      axios
        .post(`https://${Configuration.AUTH0_DOMAIN}/oauth/token`, {
          client_id: Configuration.AUTH0_CLIENT_ID,
          grant_type: 'http://auth0.com/oauth/grant-type/password-realm',
          username: email,
          password: password,
          realm: Configuration.AUTH0_REALM,
          audience: Configuration.AUTH0_AUDIENCE,
          scope: 'openid profile email offline_access',
          redirect_uri: redirectUri,
        })
        .then((response) => {
          analytics.track('Logged In', {
            'Method Used': 'Password',
            Status: 'Success',
          })
          return response
        })
        .catch((error) => {
          analytics.track('Logged In', {
            'Method Used': 'Password',
            Status: 'Failure',
          })

          const error_description = error.response.data.error_description
          return Promise.reject(
            'Email/Password login error - ' + error_description
          )
        })
    )
  }

  const refresh = (refreshToken) => {
    const validateToken = refreshToken
      ? axios.post(`https://${Configuration.AUTH0_DOMAIN}/oauth/token`, {
          client_id: Configuration.AUTH0_CLIENT_ID,
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
        })
      : Promise.reject('No token')

    return updateTokens(validateToken)
  }

  const logout = () => {
    return axios
      .post(`https://${Configuration.AUTH0_DOMAIN}/oauth/revoke`, {
        client_id: Configuration.AUTH0_CLIENT_ID,
        token: refreshToken,
      })
      .finally(() => {
        setAccessToken(null)
        setRefreshToken(null)
        return AsyncStorage.removeItem(REFRESH_TOKEN)
      })
  }

  const updateTokens = (request) => {
    return request
      .then((response) => response.data)
      .then((data) => {
        setAccessToken(data.access_token)
        setRefreshToken(data.refresh_token)
        setStatus(AuthStatus.SUCCESS)
        AsyncStorage.setItem(REFRESH_TOKEN, data.refresh_token)
        return data.access_token
      })
      .catch((error) => {
        setStatus(AuthStatus.ERROR)
        return Promise.reject(error)
      })
  }

  const resetPassword = (email: string) =>
    axios.post(
      `https://${Configuration.AUTH0_DOMAIN}/dbconnections/change_password`,
      {
        client_id: Configuration.AUTH0_CLIENT_ID,
        connection: Configuration.AUTH0_REALM,
        email,
      }
    )

  useEffect(() => {
    init()
      .then(refresh)
      .catch((ignored) => {})
  }, [])

  const refreshAccessToken = () => {
    return refresh(refreshToken)
  }
  const isAuthenticated = useMemo(
    () => accessToken && status === AuthStatus.SUCCESS,
    [accessToken, status]
  )

  return (
    <Provider
      value={{
        isAuthenticated,
        accessToken,
        refreshAccessToken,
        linkForLogin,
        loginWithCode,
        loginWithEmailPassword,
        logout,
        resetPassword,
      }}>
      {status === AuthStatus.LOADING ? <LoadingScreen /> : children}
    </Provider>
  )
}

export { AuthProvider }
export const useAuthContext = () => useContext(AuthContext)
